import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG, getAuthHeader } from '../../api/config';
import './AffiliationAdresse.css';

const AffiliationAdresse = () => {
  const [adresses, setAdresses] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    ville: '',
    cp: '',
    societe: 'legrand'
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSociete, setFilterSociete] = useState('');

  // Configuration API pour Legrand
  const headers = {
    ...API_CONFIG.headers,
    ...getAuthHeader('legrand')
  };

  // Charger toutes les adresses
  useEffect(() => {
    fetchAdresses();
  }, []);

  const fetchAdresses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/api/legrand/affiliation/adresses`,
        { headers }
      );
      // Filtrer uniquement les adresses de Legrand
      const legrandAdresses = response.data.filter(a => a.societe === 'legrand');
      setAdresses(legrandAdresses);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des adresses');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        await axios.put(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/adresses/${editingId}`,
          { ...formData, societe: 'legrand' },
          { headers }
        );
        setEditingId(null);
      } else {
        await axios.post(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/adresses`,
          { ...formData, societe: 'legrand' },
          { headers }
        );
      }

      setFormData({
        nom: '',
        adresse: '',
        ville: '',
        cp: '',
        societe: 'legrand'
      });
      fetchAdresses();
      setError('');
    } catch (err) {
      setError('Erreur lors de l\'enregistrement');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (adresse) => {
    setFormData({
      nom: adresse.nom,
      adresse: adresse.adresse,
      ville: adresse.ville,
      cp: adresse.cp,
      societe: 'legrand'
    });
    setEditingId(adresse.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette adresse?')) {
      setLoading(true);
      try {
        await axios.delete(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/adresses/${id}`,
          { headers }
        );
        fetchAdresses();
        setError('');
      } catch (err) {
        setError('Erreur lors de la suppression');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      nom: '',
      adresse: '',
      ville: '',
      cp: '',
      societe: 'legrand'
    });
  };

  // Filtrer les adresses (uniquement Legrand)
  const filteredAdresses = adresses.filter(adresse => {
    const matchSearch = adresse.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       adresse.ville.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       adresse.cp.includes(searchTerm) ||
                       adresse.adresse.toLowerCase().includes(searchTerm.toLowerCase());

    return matchSearch;
  });

  return (
    <div className="affiliation-adresse-container">
      <h2>Gestion des Adresses d'Affiliation</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Formulaire */}
      <div className="form-section">
        <h3>{editingId ? 'Modifier l\'adresse' : 'Ajouter une adresse'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Nom"
              required
              className="form-input"
            />
            <input
              type="text"
              name="societe"
              value="LEGRAND"
              disabled
              className="form-input"
              style={{ backgroundColor: '#f0f0f0' }}
            />
          </div>

          <div className="form-row">
            <textarea
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              placeholder="Adresse"
              required
              className="form-textarea"
              rows="3"
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              name="ville"
              value={formData.ville}
              onChange={handleInputChange}
              placeholder="Ville"
              required
              className="form-input"
            />
            <input
              type="text"
              name="cp"
              value={formData.cp}
              onChange={handleInputChange}
              placeholder="Code postal"
              required
              className="form-input"
              maxLength="10"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Chargement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </button>
            {editingId && (
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Filtres */}
      <div className="filter-section">
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {/* Table des adresses */}
      <div className="table-section">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Adresse</th>
              <th>Ville</th>
              <th>CP</th>
              <th>Date création</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center">Chargement...</td>
              </tr>
            ) : filteredAdresses.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">Aucune adresse trouvée</td>
              </tr>
            ) : (
              filteredAdresses.map(adresse => (
                <tr key={adresse.id}>
                  <td>{adresse.nom}</td>
                  <td>{adresse.adresse}</td>
                  <td>{adresse.ville}</td>
                  <td>{adresse.cp}</td>
                  <td>{new Date(adresse.date_creation).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(adresse)}
                      className="btn btn-sm btn-edit"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(adresse.id)}
                      className="btn btn-sm btn-delete"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AffiliationAdresse;